import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Scale, Subtract, Total } from "@pdfking/math"
import { ToPixel } from "@pdfking/quantity"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"
import type { StringGUID } from "@pdfking/tfunc"

import { PALETTE } from "#constants"
import type { Measure } from "#internal"

import { DrawPlate } from "../layout/DrawPlate"
import { PlateBox } from "../layout/PlateBox"
import { DrawPoster } from "../poster/DrawPoster"
import { CreateRandom } from "../random/CreateRandom"
import { AuthorName } from "../text/AuthorName"
import { PosterWord } from "../text/PosterWord"

// The flap folded in from the back, in `box`: on the primary a poster on a
// plate, the author's name under it. `short` is the shortest side of the
// cover's boards, which it is set by
export const DrawBackFlap = ({
  box,
  measure,
  seed,
  short,
}: {
  box: SpatialRectangle<Distance>
  measure: Measure
  seed: StringGUID
  short: Distance
}): PageShape[] => {
  const margin = Scale(short, 0.08)
  const top = Total(box.y, box.height)
  const plate = PlateBox({
    bottom: Subtract(top, Scale(box.height, 0.55)),
    center: Total(box.x, Scale(box.width, 0.5)),
    max_width: Subtract(box.width, Scale(margin, 2)),
    ratio: 0.75,
    top: Subtract(top, margin),
  })
  const author_size = Scale(short, 1 / 22)
  return [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: box },
    ...DrawPlate({
      box: plate,
      draw: inside =>
        DrawPoster({
          box: inside,
          measure,
          panels: 1,
          random: CreateRandom(seed, "back flap"),
          word: PosterWord(seed, -1),
          word_panel: 0,
        }),
      short,
    }),
    {
      enum: PlotFunction.PLOT_TEXT,
      font_size: ToPixel(author_size),
      horizontal_align: HorizontalAlign.CENTER,
      position: {
        ...box,
        height: author_size,
        y: Subtract(plate.y, Total(Scale(short, 0.04), author_size)),
      },
      text: AuthorName(seed),
      text_color: PALETTE.text,
      vertical_align: VerticalAlign.TOP,
    },
  ]
}
